<template>
	<div v-if="list.length && (view === 'all' || view === 'checklist')" class="u-stack-lg">
		<template v-if="view === 'all'">
		<CloHeading level="3" size="5" minor>{{ $t('mediaManager.checklistMedia') }}</CloHeading>
			<div class="mediaSlider">
				<button class="mediaSlider-slide" role="button" v-for="(asset, assetIndex) in list" :key="asset.assetId" @click="openLightbox($event, asset.assetId)">
					<figure>
						<template v-if="asset.mediaType === 'audio'">
							<img v-if="!asset.error" :src="mediaDownloadUrl + asset.assetId + '/default/preview'" @error="imageError(assetIndex)" :alt="asset.assetId" class="mediaSlider-audio">
							<span v-else class="processing">
								<span>
									<CloIcon symbol="timer" size="sm" />
									<span>{{ $t('media.processingAudio') }}</span>
								</span>
							</span>
							<span v-if="!asset.error" class="play">
								<CloIcon symbol="play" size="sm" />
							</span>
							<figcaption>
								<div class="text">
								<span class="bold">{{ title(asset) }}</span>
								©&nbsp;{{ asset.userDisplayName }}
								</div>
							</figcaption>
						</template>
						<template v-else-if="asset.mediaType === 'video'">
							<img v-if="!asset.error" :src="mediaDownloadUrl + asset.assetId + '/640'" @error="imageError(assetIndex)" :alt="asset.assetId">
							<span v-else class="processing">
								<span>
									<CloIcon symbol="timer" size="sm" />
									<span>{{ $t('media.processingVideo') }}</span>
								</span>
							</span>
							<span v-if="!asset.error" class="play">
								<CloIcon symbol="play" size="sm" />
							</span>
							<figcaption>
								<div class="text">
								<span class="bold">{{ title(asset) }}</span>
								©&nbsp;{{ asset.userDisplayName }}
								</div>
							</figcaption>
						</template>
						<template v-else>
							<img v-if="!asset.error" :src="mediaDownloadUrl + asset.assetId + '/640'" @error="imageError(assetIndex)"  :alt="asset.assetId">
							<span v-else class="processing">
								<span>
									<CloIcon symbol="timer" size="sm" />
									<span>{{ $t('media.processingPhoto') }}</span>
								</span>
							</span>
							<figcaption>
								<div class="text">
								<span class="bold">{{ title(asset) }}</span>
								©&nbsp;{{ asset.userDisplayName }}
								</div>
							</figcaption>
						</template>
					</figure>
				</button>
			</div>
			<CloLightbox
				v-if="open"
				reverse
				externalLightbox
				dataFormat="search2.0"
				mediaVersion="2.0"
				:list="list"
				:goTo="goTo"
				:mediaDownloadUrl="mediaDownloadUrl"
				:ebirdWebUrl="ebirdWebUrl"
				:mediaSpecimenUrl="mediaSpecimenUrl"
				ratingEnabled
				:getRatingsUrl="getRatingsUrl"
				:saveRatingUrl="saveRatingUrl"
				:ratingCsrf="ratingCsrf"
				@close="close"
			></CloLightbox>
		</template>
		<div v-else>
			<CloResultsGallery
				dataFormat="search2.0"
				mediaVersion="2.0"
				:list="list"
				:mediaDownloadUrl="mediaDownloadUrl"
				:ebirdWebUrl="ebirdWebUrl"
				:mediaSpecimenUrl="mediaSpecimenUrl"
				integratedLightbox
				ratingEnabled
				:getRatingsUrl="getRatingsUrl"
				:saveRatingUrl="saveRatingUrl"
				:ratingCsrf="ratingCsrf"
				:rowHeightForSmall="180"
				:rowHeightForMedium="view === 'checklist' ? 360 : 180"
				:responsiveImages="false"
			></CloResultsGallery>
		</div>
	</div>
</template>

<script>
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloResultsGallery from '@/media/CloResultsGallery/CloResultsGallery.vue';
import CloLightbox from '@/media/CloLightbox/CloLightbox.vue';

export default {
    name: 'ChecklistReportChecklistMedia',
    components: { CloResultsGallery, CloHeading, CloIcon, CloLightbox },
    data() {
        return {
			checklistMediaJson: window.checklistMediaJson,
			checklistMediaDisplayOrder: window.checklistMediaDisplayOrder,
			list: [],
			view: 'all',
			open: false,
			goTo: null,
			opener: null,
			categories: [
				'environmental',
				'experience',
				'other_animals'
			]
        };
    },
    props: {
        mediaDownloadUrl: {
            type: String,
            required: true,
        },
        ebirdWebUrl: {
            type: String,
            required: true,
        },
        mediaSpecimenUrl: {
            type: String,
            required: true,
        },
		getRatingsUrl: {
			type: String,
			required: true,
		},
		saveRatingUrl: {
			type: String,
			required: true,
		},
		ratingCsrf: {
			type: String,
			required: true,
		},
		subId: {
			type: String,
			required: true,
		},
		baseUrl: {
			type: String,
			required: true,
		},
		mediaApi: {
			type: String,
			required: true,
		}
    },
	beforeMount() {
		// get current view
		const query = new URLSearchParams(window.location.search);
		const view = query.get('view');
		this.view = view ? view : 'all'
	},
    mounted() {
		this.prepareMediaData();
		// listen for changes in view
		document.addEventListener('checklist-report-view', (e) => {
			this.view = e.detail.view;
		});
	},
	methods: {
		prepareMediaData() {
			// get location name from html on page
			const locationName = document.querySelector('[data-locationname]').textContent;
			// sort media by participant with checklist owner's media first
			const participants = document.querySelectorAll('[data-participant-userid]')
			const participantIds = Array.prototype.map.call(participants, (participant) => {
				return participant.getAttribute('data-participant-userid');
			});
			// set display order, sort by category, user, display order, then asset id
			this.list = this.checklistMediaJson.map((a) => {
				a.displayOrder = this.checklistMediaDisplayOrder[a.assetId] || 0;
				return a;
			}).sort((a, b) => {
				if (participantIds.indexOf(a.userId) < participantIds.indexOf(b.userId)) {
					return - 1
				}
				if (participantIds.indexOf(a.userId) > participantIds.indexOf(b.userId)) {
					return 1
				}
				// sort by category
				const aCategory = this.categories.find((category) => 'assetTagCodes' in a && a.assetTagCodes.includes(category));
				const bCategory = this.categories.find((category) => 'assetTagCodes' in b && b.assetTagCodes.includes(category));
				if (aCategory && bCategory) {
					if (this.categories.indexOf(aCategory) < this.categories.indexOf(bCategory)) {
						return - 1
					}
					if (this.categories.indexOf(aCategory) > this.categories.indexOf(bCategory)) {
						return 1
					}
				}
				if (a.displayOrder < b.displayOrder) {
					return -1;
				}
				if (a.displayOrder > b.displayOrder) {
					return 1;
				}
				if (a.assetId < b.assetId) {
					return -1;
				}
				if (a.assetId > b.assetId) {
					return 1;
				}
				return 0;
			}).map((asset) => {
				asset.ebirdChecklistId = this.subId;
				asset.mediaType = asset.assetFormatCode;
				asset.tags = asset.assetTagCodes;
				asset.taxonomy = asset.taxon ? asset.taxon : { speciesCode: null };
				asset.valid = true;
				// get user information from html on page
				const userElement = document.querySelector('[data-participant-userid="' + asset.userId + '"]');
				const userDisplayName = userElement.querySelector('[data-participant-userdisplayname]').textContent;
				asset.userDisplayName = userDisplayName;
				asset.userHasProfile = userElement.getAttribute('data-participant-showprofilelink') === 'true';
				// date from global variable on page
				asset.obsDt = window.chkDateDisplayDate;
				asset.obsDtDisplay = window.chkDateDisplayDate;
				asset.mediaNotes = asset.comments;
				asset.location = { 
					name: locationName,
					subnational2Name: null,
					subnational1Name: null,
					countryName: null
				};
				asset.error = false
                asset.rating = asset.ratingAverage;
                asset.ratingCount = asset.ratingCount;
				return asset;
			});
		},
		imageError(assetIndex) {
			this.$set(this.list[assetIndex], 'error', true)
			this.$forceUpdate();
		},
		openLightbox(e, assetId) {
			this.goTo = assetId
			this.open = true
			this.opener = e.target
		},
		close() {
			this.open = false;
			if (this.opener) {
				this.opener.focus();
			}
			this.opener = null;
		},
		title(asset) {
			let title = [];
			if (asset.tags) {
				if (asset.tags.includes('environmental')) {
					if (asset.mediaType === 'photo') {
						title.push(this.$t('mediaTag.tags.habitatPhoto'));
					}
					if (asset.mediaType === 'audio') {
						title.push(this.$t('mediaTag.tags.soundscape'));
					}
					if (asset.mediaType === 'video') {
						title.push(this.$t('mediaTag.tags.environmentalVideo'));
					}
				}
				else if (asset.tags.includes('experience')) {
					title.push(this.$t('mediaTag.tags.experience'));
				}
				else if (asset.tags.includes('other_animals')) {
					if (asset.taxon && asset.taxon.comName) {
						title.push(asset.taxon.comName);
					}
					else {
						title.push(this.$t('mediaTag.tags.other_animals'));
					}
				}
			}
			if (asset.caption) {
				title.push(asset.caption);
			}
			return title.join(': ');
		}
    },
}
</script>

<style scoped lang="scss">
.mediaSlider {
	display: flex;
	flex-wrap: nowrap;
	justify-content: flex-start;
	gap: 1px;
	overflow-x: auto;
	width: auto;
	scroll-snap-type: x mandatory;
	scroll-behavior: smooth;
	// hide the scrollbar
	scrollbar-width: none;
	-ms-overflow-style: none;
	// add fade effect on the right
	mask-image: linear-gradient(to right, #000 97%, rgba(0, 0, 0, .1));
	padding-right: 1rem;
	&::-webkit-scrollbar {
		width: 0;
		height: 0;
	}
	&-slide {
		flex: 0 0 auto;
		position: relative;
		scroll-snap-align: center;
		scroll-snap-stop: always;
		text-align: left;
		img {
			width: auto;
			height: 90px;
			&.mediaSlider-audio {
				aspect-ratio: 32 / 11;
			}
		}
		figcaption {
			opacity: 0;
			transition: opacity 0.3s;
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0;
			@include text(1);
			@include inset-squish-sm;
			color: $color-white;
			background-color: $color-overlay-black;
			.text {
				display: -webkit-box;
				-webkit-line-clamp: 3;
				-webkit-box-orient: vertical;
				overflow: hidden;
			}
			.bold {
				font-weight: bold;
			}
		}
		&:hover figcaption {
			opacity: 1;
		}
		.play {
			position: absolute;
			bottom: $space-sm;
			left: $space-sm;
			background-color: $color-primary-3;
			color: $color-white;
			border-radius: $radius-sm;
			@include inset-squish-sm;
			line-height: 0;
		}
		.processing {
			height: 90px;
			background-color: $color-neutral-2;
			display: flex;
			justify-content: center;
			align-items: center;
			color: $color-neutral-5;
			@include inset-sm;
			@include text(1);
			.Icon {
				fill: $color-neutral-3;
			}
		}
	}
}
</style>